import { LngLat } from "mapbox-gl";
import { useCallback, useMemo } from "react";
import { useMap } from "react-map-gl";
import { useMapStore, usePropertyStore } from "..";
import IProperty from "../interfaces/IProperty";
import MapboxApiService from "../services/mapboxApiService";

export default function usePropertySearch() {
    const map = useMap().map;
    const mapboxApiService = useMemo(() => new MapboxApiService(), []);
    const propertyStore = usePropertyStore();
    const mapStore = useMapStore();

    const fetchPropertyByCoordinate = useCallback(async (lngLat: LngLat): Promise<IProperty | undefined> => {
        if (!map) return undefined;
        await propertyStore.fetchProperty(lngLat, map);
        return propertyStore.getProperty(lngLat);
    }, [map, propertyStore]);

    const searchByAddress = useCallback(async (address: string): Promise<IProperty | undefined> => {
        try {
            const locations = await mapboxApiService.addressLocationSearch(address, new LngLat(mapStore.viewState.longitude, mapStore.viewState.latitude), 1);
            if (!locations.length)
                return undefined;

            return await fetchPropertyByCoordinate(new LngLat(locations[0].coordinate.longitude, locations[0].coordinate.latitude));
        } catch (ex) {
            return undefined;
        }
    }, [mapboxApiService, mapStore.viewState.longitude, mapStore.viewState.latitude, fetchPropertyByCoordinate]);

    const searchByLngLat = useCallback(async (lngLat: LngLat): Promise<IProperty | undefined> => {
        try {
            return await fetchPropertyByCoordinate(lngLat);
        } catch (ex) {
            return undefined;
        }
    }, [fetchPropertyByCoordinate]);

    return useMemo(() => ({
        searchByAddress,
        searchByLngLat,
    }), [searchByAddress, searchByLngLat]);
};
